/**
 * The rules that build a set of definitions, held in one place and applied before any write.
 *
 * An element is a set-level idea: one part across every drawing it appears on, so placing
 * it, titling it, confirming it and dropping it when nothing pins it any more are all
 * decisions about the whole set rather than about a figure. That is why these take and
 * return the entire set. Digits are projected from registry order; they are not identity.
 *
 * The API only ever refuses a set it will not store; it never builds one. So these rules
 * exist once, here, rather than a second time in Python where nothing would force the two to
 * agree. What holds them to the contract is a property in this module's suite: no operation
 * may produce a set `refuse_unless_definable` would reject.
 *
 * Every operation is idempotent. Each names the outcome it wants rather than the step it
 * takes, so applying one twice changes nothing the first application did not already do.
 * The write seam depends on this: it rebuilds the body against the freshest set when its turn
 * in the queue comes, over a cache that already carries the same change optimistically.
 *
 * ## Why this file is long
 *
 * It is past the length limit the rest of the package keeps to, and `eslint.config.js` names
 * it with the reasoning. Three seams were tried. The connector rules read `clamped` and
 * `instancesOf`, which are private here, and `place` mints a connector, so lifting them out
 * leaves an import cycle. The pure geometry and the read functions at the tail each come away
 * cleanly but are only sixty and ninety lines, and taking both would scatter one set of rules
 * across three files without clearing the limit either.
 *
 * The cohesion is the design rather than an accident: every operation takes and returns the
 * whole set, because an element is a set-level idea. Read it as one thing. If a redesign ever
 * makes a seam fall out naturally, take it and delete the exemption.
 */

import type {
  FigureDefinitionsRequest,
  FigureListResponse,
  PointModel,
} from "@patent-studio/api-types";
import { blank } from "./figure-status";
import { registryAfter } from "./figure-registry";
import { membership, numeralsOf } from "./numbering";

/**
 * The definitions with the optionality resolved.
 *
 * `registry` and `figures` are optional on the wire because each has a server-side default,
 * and absent and empty say the same thing, so they are collapsed rather than branched on at
 * every use.
 */
export type ConnectorDefaults = Required<
  NonNullable<
    NonNullable<FigureDefinitionsRequest["settings"]>["connector_defaults"]
  >
>;

/**
 * The conventions the set is made under, with every field present.
 *
 * Resolved here rather than branched on, for the same reason the two lists below are: each
 * field has a server-side default, so absent and defaulted say the same thing, and a reader
 * that had to ask which would answer the question once per use.
 */
export type FigureSettings = {
  connector_defaults: ConnectorDefaults;
  numeral_style: NonNullable<
    NonNullable<FigureDefinitionsRequest["settings"]>["numeral_style"]
  >;
  numbering_convention: NonNullable<
    NonNullable<FigureDefinitionsRequest["settings"]>["numbering_convention"]
  >;
  confirmed: boolean;
};

export type Definitions = Required<
  Pick<FigureDefinitionsRequest, "registry" | "figures" | "version">
> & { settings: FigureSettings };

export type ElementRow = Definitions["registry"][number];
export type FigureRow = Definitions["figures"][number];
export type Instance = NonNullable<FigureRow["instances"]>[number];
export type Connector = NonNullable<Instance["connector"]>;
export type NumeralStyle = FigureSettings["numeral_style"];
export type NumberingConvention = FigureSettings["numbering_convention"];

/**
 * The conventions a set is made under before anybody changes them.
 *
 * Written here as well as in the stored model, because the browser reads a set the moment it
 * arrives and a set stored before settings existed carries none. Both spellings of the same
 * ruling, and the endpoint suite is what holds them together.
 */
export const RULED_SETTINGS: FigureSettings = {
  connector_defaults: { shape: "straight", anchors: 1, arrowhead: false },
  numeral_style: "plain",
  numbering_convention: "even",
  confirmed: false,
};

/**
 * The conventions a served set was made under, field by field.
 *
 * Filled one field at a time rather than taken whole, because a set stored before any of
 * this existed carries no settings at all, and one stored between two of these fields
 * arriving carries some of them. Either way what comes back is the conventions the drawing
 * is read under, which every reader below can rely on being complete.
 */
export function settingsOf(
  stored:
    Partial<NonNullable<FigureDefinitionsRequest["settings"]>> | undefined,
): FigureSettings {
  return {
    connector_defaults: {
      ...RULED_SETTINGS.connector_defaults,
      ...stored?.connector_defaults,
    },
    numeral_style: stored?.numeral_style ?? RULED_SETTINGS.numeral_style,
    numbering_convention:
      stored?.numbering_convention ?? RULED_SETTINGS.numbering_convention,
    confirmed: stored?.confirmed ?? RULED_SETTINGS.confirmed,
  };
}

/**
 * Whether an instance is the same mark as a proposed one.
 *
 * The id is the identity, but the same element at the identical fraction of a drawing is
 * still the same mark whatever its id says: it is one annotation made twice, not two.
 */
function sameMark(
  instance: Instance,
  elementId: string,
  at: PointModel,
): boolean {
  return (
    instance.element_id === elementId &&
    instance.pin.x === at.x &&
    instance.pin.y === at.y
  );
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * A position brought inside the drawing.
 *
 * Every rule that takes a position from the attorney passes it through here, because this
 * module promises never to build a set the API would refuse and a position outside the
 * drawing is one of the things it refuses, over the whole set rather than the one mark. A
 * drag holds the pointer, so positions past the edge arrive as a matter of course. Clamping
 * one that is already inside returns it unchanged, so a change re-applied still settles.
 */
function clamped(point: PointModel): PointModel {
  return { x: clamp01(point.x), y: clamp01(point.y) };
}

function instancesOf(row: FigureRow): Instance[] {
  return row.instances ?? [];
}

/** Every element pinned anywhere on the set. */
function pinnedElementIds(definitions: Definitions): Set<string> {
  return new Set(
    definitions.figures.flatMap((figure) =>
      instancesOf(figure).map((i) => i.element_id),
    ),
  );
}

/**
 * The set with any registry row nothing pins removed, and any group left with fewer than two
 * members dissolved, by the rule `registryAfter` states for both languages.
 *
 * Run after anything that can leave an element behind. A registry that accumulates ids no
 * figure uses stops being a statement of what the set contains, and the attorney cannot tell
 * a deliberate gap from a leftover. The API refuses such a set outright.
 */
function withoutOrphans(definitions: Definitions): Definitions {
  const kept = registryAfter(
    definitions.registry,
    pinnedElementIds(definitions),
  );
  if (kept.length === definitions.registry.length) return definitions;
  return { ...definitions, registry: kept };
}

/**
 * The set with this figure's row replaced, keeping the order the figures arrived in.
 *
 * Exported for the write seam, which builds the figure-level changes that are not about
 * numbering at all, so that a description save and a placement put a row back the same way.
 */
export function withFigure(
  definitions: Definitions,
  fileId: string,
  row: FigureRow,
): Definitions {
  const known = definitions.figures.some((figure) => figure.file_id === fileId);
  const figures = known
    ? definitions.figures.map((figure) =>
        figure.file_id === fileId ? row : figure,
      )
    : [...definitions.figures, row];
  return { ...definitions, figures };
}

/**
 * This drawing's definition, or the one it would have the moment something is put on it.
 *
 * Numbered either way, because a drawing that carries a pin carries numbered elements, and
 * the status projection reads that flag rather than counting the pins. The flag matters most
 * on a row that already exists: a figure described and walked through before anything was
 * pinned to it has one saying otherwise, and it would go on reporting as complete with
 * unconfirmed elements sitting on the drawing. The API stores such a set without complaint,
 * so nothing but this catches it.
 */
function figureRow(definitions: Definitions, fileId: string): FigureRow {
  const existing = definitions.figures.find(
    (figure) => figure.file_id === fileId,
  );
  return {
    file_id: fileId,
    description: "",
    walkthrough: "",
    walkthrough_file_ids: [],
    walkthrough_accepted: false,
    instances: [],
    prior_art: false,
    ...existing,
    numbered: true,
  };
}

/** The set with this element registered if it is new, and untouched if it is not. */
function withRegistered(
  definitions: Definitions,
  elementId: string,
): Definitions {
  if (definitions.registry.some((row) => row.element_id === elementId))
    return definitions;
  return {
    ...definitions,
    registry: [
      ...definitions.registry,
      { element_id: elementId, title: "", confirmed: false, kind: "element" },
    ],
  };
}

function withRow(
  definitions: Definitions,
  elementId: string,
  row: ElementRow,
): Definitions {
  return {
    ...definitions,
    registry: definitions.registry.map((r) =>
      r.element_id === elementId ? row : r,
    ),
  };
}

/**
 * Pin an element to a drawing, registering it if the set has not seen it.
 *
 * An element the set already knows keeps its shared title, which is what makes reuse across
 * figures free rather than a second code path. The caller mints `elementId`, the same way it
 * mints the pin's own id: digits are projected later and are not this identity.
 *
 * The id arrives settled, minted when the attorney clicked, so a placement applied to a set
 * that already carries it is the same placement arriving twice and changes nothing. The same
 * goes for a second id of the same element at the same point: one mark, made twice.
 */
export function place(
  definitions: Definitions,
  {
    fileId,
    id,
    elementId,
    pin,
    tip,
    defaults,
  }: {
    fileId: string;
    id: string;
    elementId: string;
    pin: PointModel;
    tip: PointModel;
    defaults: ConnectorDefaults;
  },
): Definitions {
  const at = clamped(pin);
  const row = figureRow(definitions, fileId);
  const already = instancesOf(row).some(
    (i) => i.id === id || sameMark(i, elementId, at),
  );
  if (already) return definitions;

  const placed: Instance = {
    id,
    element_id: elementId,
    pin: at,
    connector: connectorFrom(defaults, at, clamped(tip)),
  };
  const withPin = withFigure(definitions, fileId, {
    ...row,
    instances: [...instancesOf(row), placed],
  });
  return withRegistered(withPin, elementId);
}

/**
 * Give an element its title, everywhere at once.
 *
 * Free, because the title belongs to the registry row rather than to any pin. Confirmation
 * clears with the edit: a confirmed element states that somebody read that title, and the
 * title is no longer the one they read.
 */
export function retitle(
  definitions: Definitions,
  elementId: string,
  title: string,
): Definitions {
  const row = definitions.registry.find((r) => r.element_id === elementId);
  if (row === undefined) return definitions;
  return withRow(definitions, elementId, { ...row, title, confirmed: false });
}

/**
 * Confirm an element, which confirms it on every figure it appears on.
 *
 * An untitled element is left alone, the rule confirm-all already follows, applied at this
 * door too. Confirming states that somebody read the title, and the API stores a confirmed
 * blank without complaint, so nothing further down would catch one. It matters most when a
 * confirmation arrives behind a title that failed to save: what is on screen is not what the
 * set holds, and the set is what this is confirming.
 */
export function confirmElement(
  definitions: Definitions,
  elementId: string,
): Definitions {
  const row = definitions.registry.find((r) => r.element_id === elementId);
  if (row === undefined || blank(row.title)) return definitions;
  return withRow(definitions, elementId, { ...row, confirmed: true });
}

/**
 * Confirm every titled element on one drawing, leaving the untitled ones alone.
 *
 * Not a select-all. An untitled row has nothing to confirm, and confirming it would state
 * that a blank title had been read and accepted.
 */
export function confirmTitledOn(
  definitions: Definitions,
  fileId: string,
): Definitions {
  const here = definitions.figures.find((figure) => figure.file_id === fileId);
  if (here === undefined) return definitions;
  const hereIds = new Set(instancesOf(here).map((i) => i.element_id));
  return {
    ...definitions,
    registry: definitions.registry.map((row) =>
      hereIds.has(row.element_id) && !blank(row.title)
        ? { ...row, confirmed: true }
        : row,
    ),
  };
}

/**
 * The instances with any mark that repeats an earlier one dropped, keeping the earlier id.
 *
 * An element and a point are one mark, so a second copy of the pair is the same mark recorded
 * twice rather than a second mark. Only an adopt (or a merge onto displayed digits) can
 * produce one, by moving a pin onto an element already sitting at that exact point, and the
 * attorney asked for one mark there either way.
 */
function withoutRepeats(instances: Instance[]): Instance[] {
  const seen = new Set<string>();
  return instances.filter((instance) => {
    const key = `${instance.element_id}@${instance.pin.x},${instance.pin.y}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Move one pin onto an element the set already holds, named by the pin's id.
 *
 * The surviving element keeps its title. The element left behind goes if nothing pins it any
 * more. An id the registry does not know is not a new element here: that is `place`'s job,
 * and typing a free number is `setOverride`.
 */
export function adopt(
  definitions: Definitions,
  {
    fileId,
    id,
    toElementId,
  }: { fileId: string; id: string; toElementId: string },
): Definitions {
  if (!definitions.registry.some((row) => row.element_id === toElementId))
    return definitions;
  const row = definitions.figures.find((figure) => figure.file_id === fileId);
  if (row === undefined) return definitions;
  if (!instancesOf(row).some((i) => i.id === id)) return definitions;

  const moved = withFigure(definitions, fileId, {
    ...row,
    instances: withoutRepeats(
      instancesOf(row).map((i) =>
        i.id === id ? { ...i, element_id: toElementId } : i,
      ),
    ),
  });
  return withoutOrphans(moved);
}

/**
 * Hand every pin of one element to another, then drop the row nothing pins.
 *
 * Used when typed digits already name a part: the two rows would otherwise file the same
 * number, which the projection cannot represent and the API refuses.
 */
function mergeInto(
  definitions: Definitions,
  fromElementId: string,
  toElementId: string,
): Definitions {
  const retargeted: Definitions = {
    ...definitions,
    figures: definitions.figures.map((figure) => ({
      ...figure,
      instances: withoutRepeats(
        instancesOf(figure).map((i) =>
          i.element_id === fromElementId
            ? { ...i, element_id: toElementId }
            : i,
        ),
      ),
    })),
  };
  return withoutOrphans(retargeted);
}

/**
 * Set the digits this element shows, without changing which element it is.
 *
 * The office-action seam: Define does not offer this. A later locked set uses
 * it when the attorney must place a specific number the convention would not
 * give.
 *
 * Digits another element already shows are a reuse, not an override: every pin of this
 * element joins that one, and this row goes. Digits this element already shows are left
 * alone, so a derived number is never frozen as an override. A free number is written on
 * the same row, title and confirmation kept.
 *
 * Two consequences the merge does not choose, because numbers come from position in the
 * set rather than from this function: the set is one element shorter, so every derived
 * number after the gap moves down one term, and the number that was typed is therefore
 * not always the number the surviving element ends up showing. The absorbed element's
 * title goes with its row.
 *
 * A member is left alone: it shows its group's numeral and a letter, and takes no number of
 * its own. A group takes the digits like an element, and its members follow them. Only a
 * plain element is ever merged into: a group showing the typed digits is a set, not a part
 * the element could be, so the element takes the override and the group moves on.
 */
export function setOverride(
  definitions: Definitions,
  elementId: string,
  value: number,
): Definitions {
  const row = definitions.registry.find((r) => r.element_id === elementId);
  if (row === undefined || membership(definitions.registry)(row) !== null)
    return definitions;

  const assigned = numeralsOf(definitions);
  if (assigned.get(elementId) === String(value)) return definitions;

  const shows =
    row.kind === "group"
      ? undefined
      : definitions.registry.find(
          (other) =>
            other.element_id !== elementId &&
            other.kind !== "group" &&
            assigned.get(other.element_id) === String(value),
        );
  if (shows !== undefined)
    return mergeInto(definitions, elementId, shows.element_id);

  return withRow(definitions, elementId, {
    ...row,
    numeral_override: value,
  });
}

/**
 * Let an element go back to the number its place in the set gives it.
 *
 * Without this a number typed once could never be untyped, and an override entered by
 * mistake would outlive every convention the set is ever read under.
 */
export function clearOverride(
  definitions: Definitions,
  elementId: string,
): Definitions {
  const row = definitions.registry.find((r) => r.element_id === elementId);
  if (row === undefined || row.numeral_override == null) return definitions;
  return withRow(definitions, elementId, { ...row, numeral_override: null });
}

/**
 * Take one pin off a drawing.
 *
 * The instance goes, not the element: the same element legitimately appears on several
 * figures, and removing a pin on one drawing must not strip that part from the rest of the
 * set. The element leaves only when its last instance anywhere is gone.
 */
export function removeInstance(
  definitions: Definitions,
  { fileId, id }: { fileId: string; id: string },
): Definitions {
  const row = definitions.figures.find((figure) => figure.file_id === fileId);
  if (row === undefined) return definitions;
  const kept = instancesOf(row).filter((i) => i.id !== id);
  if (kept.length === instancesOf(row).length) return definitions;
  return withoutOrphans(
    withFigure(definitions, fileId, { ...row, instances: kept }),
  );
}

/**
 * How far below a pin a restored leader line reaches before the attorney aims it.
 *
 * A removed connector took its endpoint with it, so putting one back has to invent one. This
 * is a starting point to drag from rather than a guess at what the attorney meant.
 */
const CONNECTOR_DROP = 0.12;

/** How far a freshly curved line stands off the straight run between its two ends. */
const BEND_OFFSET = 0.1;

/**
 * Where a restored leader line points.
 *
 * Below the pin, or above it when the pin sits too close to the bottom for the drop to fit.
 * Clamping a tip that overran would land it on the pin, leaving a line of no length and no
 * handle to grab, which is worse than the flip.
 */
function defaultTip(pin: PointModel): PointModel {
  const below = pin.y + CONNECTOR_DROP;
  return clamped({ x: pin.x, y: below > 1 ? pin.y - CONNECTOR_DROP : below });
}

/**
 * Where a freshly curved line bends.
 *
 * Perpendicular to the run between the ends, so the curve reads as one at any angle rather
 * than only on the diagonal. Ends that coincide give no direction to stand off from, so the
 * bend starts at the midpoint and the attorney drags the one they want.
 */
function defaultControl(pin: PointModel, tip: PointModel): PointModel {
  const midpoint = { x: (pin.x + tip.x) / 2, y: (pin.y + tip.y) / 2 };
  const run = { x: tip.x - pin.x, y: tip.y - pin.y };
  const length = Math.hypot(run.x, run.y);
  if (length === 0) return clamped(midpoint);
  return clamped({
    x: midpoint.x - (run.y / length) * BEND_OFFSET,
    y: midpoint.y + (run.x / length) * BEND_OFFSET,
  });
}

/**
 * Where a curve bends when two anchor points are asked for.
 *
 * A third and two thirds along the run, offset to opposite sides of it, which is what makes
 * the S read as one at any angle. The single-anchor bend sits at the midpoint offset to one
 * side, so the two share a convention rather than each inventing a shape.
 */
function defaultControls(pin: PointModel, tip: PointModel): PointModel[] {
  const run = { x: tip.x - pin.x, y: tip.y - pin.y };
  const length = Math.hypot(run.x, run.y);
  const along = (t: number, side: number) => {
    const on = { x: pin.x + run.x * t, y: pin.y + run.y * t };
    if (length === 0) return clamped(on);
    return clamped({
      x: on.x - (run.y / length) * BEND_OFFSET * side,
      y: on.y + (run.x / length) * BEND_OFFSET * side,
    });
  };
  return [along(1 / 3, 1), along(2 / 3, -1)];
}

/**
 * The leader line a mark is born with, under the conventions in force when it was made.
 *
 * The defaults are passed in rather than read from the set, because a change waits in the
 * queue and is folded again over whatever set is freshest when its turn comes. Read here, a
 * replay after the attorney changed the conventions would mint a connector under conventions
 * they never saw for a mark they had already placed.
 */
function connectorFrom(
  defaults: ConnectorDefaults,
  pin: PointModel,
  tip: PointModel,
): Connector {
  if (defaults.shape === "straight")
    return { shape: "straight", tip, arrowhead: defaults.arrowhead };
  return {
    shape: "curved",
    tip,
    controls:
      defaults.anchors === 2
        ? defaultControls(pin, tip)
        : [defaultControl(pin, tip)],
    arrowhead: defaults.arrowhead,
  };
}

/**
 * Set the leader line every mark placed from now on is born with.
 *
 * Every existing connector is left exactly as it is. The conventions describe marks that do
 * not exist yet, and a mark the attorney has already shaped by hand is their decision rather
 * than an instance of the convention: reshaping the set would discard that work with no undo.
 */
export function setConnectorDefaults(
  definitions: Definitions,
  defaults: ConnectorDefaults,
): Definitions {
  return {
    ...definitions,
    settings: { ...definitions.settings, connector_defaults: defaults },
  };
}

/**
 * Set the sequence the projected numerals count in.
 *
 * Unlike connector defaults and numeral style, this is meant to relabel marks already on
 * the drawings: the digits are not stored, so a one-field write is the whole change. A
 * numeral_override, the office-action seam, is a different field and is left alone. Define
 * does not offer that field, so a switch here relabels every element the attorney can see.
 */
export function setNumberingConvention(
  definitions: Definitions,
  numbering_convention: NumberingConvention,
): Definitions {
  if (definitions.settings.numbering_convention === numbering_convention) {
    return definitions;
  }
  return {
    ...definitions,
    settings: { ...definitions.settings, numbering_convention },
  };
}

/**
 * How one mark is drawn: its own style when it has one, the set's when it has not.
 *
 * The only place the two are resolved, so nothing reads a mark's field raw and takes an
 * absent one for a decision to draw it plain.
 */
export function styleOf(
  instance: Pick<Instance, "numeral_style">,
  settings: FigureSettings,
): NumeralStyle {
  return instance.numeral_style ?? settings.numeral_style;
}

/**
 * Set what a mark placed from now on is drawn as.
 *
 * Every mark already on a drawing keeps the look it had, which is what the second half of
 * this does: a mark that was following the set is given the style that was in force, so it
 * goes on being drawn that way once the set has moved. The alternative restyles a drawing
 * the attorney has been marking up for an hour, on one click, with nothing to undo it.
 *
 * The same bargain the connector defaults strike, and for the same reason: a convention
 * describes marks that do not exist yet.
 */
export function setNumeralStyle(
  definitions: Definitions,
  numeral_style: NumeralStyle,
): Definitions {
  const held = definitions.settings.numeral_style;
  // A radio group issues a choice on every click, the checked one included, and an arrow key
  // that wraps issues two. Stamping on those would pin every mark on every drawing to the
  // style it is already drawn under, permanently and with nothing on screen to say so.
  if (numeral_style === held) return definitions;
  return {
    ...definitions,
    figures: definitions.figures.map((figure) => ({
      ...figure,
      instances: (figure.instances ?? []).map((instance) =>
        instance.numeral_style === null || instance.numeral_style === undefined
          ? { ...instance, numeral_style: held }
          : instance,
      ),
    })),
    settings: { ...definitions.settings, numeral_style },
  };
}

/**
 * Draw one mark against the set, leaving the set and every other mark as they are.
 *
 * Through the same seam as every other rule that names one mark, so a change queued behind
 * the one that removed that mark, or a mark asked for the style it already carries, settles
 * as the set it was given rather than as a rebuilt copy the write seam would send.
 */
export function setMarkNumeralStyle(
  definitions: Definitions,
  {
    fileId,
    id,
    numeral_style,
  }: { fileId: string; id: string; numeral_style: NumeralStyle },
): Definitions {
  return withInstance(definitions, fileId, id, (instance) => ({
    ...instance,
    numeral_style,
  }));
}

function samePoint(one: PointModel, other: PointModel): boolean {
  return one.x === other.x && one.y === other.y;
}

/**
 * Whether two curves bend the same way.
 *
 * Position by position rather than as a set, because the order is the shape: the same two
 * bends read the other way round describe the mirrored S, which is a different curve.
 */
function sameBend(one: PointModel[], other: PointModel[]): boolean {
  return (
    one.length === other.length &&
    one.every((point, at) => samePoint(point, other[at]))
  );
}

function sameLine(
  one: Instance["connector"],
  other: Instance["connector"],
): boolean {
  if (one == null || other == null) return (one == null) === (other == null);
  if (one.shape !== other.shape || one.arrowhead !== other.arrowhead)
    return false;
  if (!samePoint(one.tip, other.tip)) return false;
  if (one.shape === "curved" && other.shape === "curved")
    return sameBend(one.controls, other.controls);
  return true;
}

/** Whether a rule's outcome is the state the mark already holds. */
function settled(one: Instance, other: Instance): boolean {
  return (
    one.element_id === other.element_id &&
    samePoint(one.pin, other.pin) &&
    // Compared raw rather than resolved against the set: a mark pinned to the style it was
    // already being drawn under is a decision, and one that has never been pinned is not.
    (one.numeral_style ?? null) === (other.numeral_style ?? null) &&
    sameLine(one.connector, other.connector)
  );
}

/**
 * The set with one mark replaced by whatever `shaped` makes of it.
 *
 * Every rule below has the same shape: find the mark, decide what it becomes, put it back.
 * Handing the same instance back is how a rule states a precondition it does not meet, and
 * the set then comes back untouched rather than rebuilt, so a change that settles nothing
 * cannot be told apart from one that was never made.
 *
 * Outcomes are also compared structurally, so idempotency is this seam's property rather
 * than a discipline each rule remembers: a rule may freely rebuild an identical instance
 * and the set still comes back untouched. The write seam re-applies changes to the
 * freshest set, and a rebuilt-but-identical set would read as a change worth writing.
 */
function withInstance(
  definitions: Definitions,
  fileId: string,
  id: string,
  shaped: (instance: Instance) => Instance,
): Definitions {
  const row = definitions.figures.find((figure) => figure.file_id === fileId);
  if (row === undefined) return definitions;
  const instances = instancesOf(row);
  const found = instances.find((i) => i.id === id);
  if (found === undefined) return definitions;
  const changed = shaped(found);
  if (changed === found || settled(changed, found)) return definitions;
  return withFigure(definitions, fileId, {
    ...row,
    instances: instances.map((i) => (i.id === id ? changed : i)),
  });
}

/** Which of the two leader lines a connector is. */
export type ConnectorShape = NonNullable<Instance["connector"]>["shape"];

/**
 * The leader lines the toolbar offers, which is the stored shape with its anchors spelled out.
 *
 * A curve at one anchor and a curve at two are one stored shape, and the attorney picks
 * between them as if they were two: a bow and an S are different lines to draw, and asking
 * for one by naming a shape and then a number would make the toolbar describe the storage
 * rather than the drawing. Absence is a kind here for the same reason, because the control
 * that takes a line away is the control that gives one back.
 */
export type LineKind = "none" | "straight" | "curved" | "s-curve";

/** Which of those four lines a mark is drawn with. */
export function lineKindOf(instance: Instance): LineKind {
  const connector = instance.connector;
  if (connector == null) return "none";
  if (connector.shape === "straight") return "straight";
  return connector.controls.length >= 2 ? "s-curve" : "curved";
}

/**
 * Move a mark to another part of the drawing.
 *
 * The line follows without being touched: both of its ends are stored, and only one of them
 * moved. Aiming the other end is `aimTip`.
 */
export function movePin(
  definitions: Definitions,
  { fileId, id, pin }: { fileId: string; id: string; pin: PointModel },
): Definitions {
  const at = clamped(pin);
  return withInstance(definitions, fileId, id, (instance) => ({
    ...instance,
    pin: at,
  }));
}

/** Point the far end of a leader line somewhere else, keeping any bend it carries. */
export function aimTip(
  definitions: Definitions,
  { fileId, id, tip }: { fileId: string; id: string; tip: PointModel },
): Definitions {
  const at = clamped(tip);
  return withInstance(definitions, fileId, id, (instance) => {
    const connector = instance.connector;
    if (connector == null) return instance;
    return { ...instance, connector: { ...connector, tip: at } };
  });
}

/**
 * Set where a curve bends.
 *
 * Only a curved line has anywhere to keep this. The stored model refuses a straight one that
 * carries a bend, deliberately, so that a straight line can never hold a curve nobody drew.
 */
export function bend(
  definitions: Definitions,
  {
    fileId,
    id,
    index,
    control,
  }: { fileId: string; id: string; index: number; control: PointModel },
): Definitions {
  const at = clamped(control);
  return withInstance(definitions, fileId, id, (instance) => {
    const connector = instance.connector;
    if (connector == null || connector.shape !== "curved") return instance;
    // Named by position, because a two-anchor curve has two handles and a drag on either
    // would otherwise write the same control, straightening the half nobody touched. A
    // position the curve does not have settles as nothing rather than appending: a queued
    // change can name the second bend of a curve since reshaped to one, and a third control
    // is a set the API refuses.
    if (index < 0 || index >= connector.controls.length) return instance;
    return {
      ...instance,
      connector: {
        ...connector,
        controls: connector.controls.map((point, on) =>
          on === index ? at : point,
        ),
      },
    };
  });
}

/**
 * Straighten a line or curve it.
 *
 * A line already of that shape is left exactly as it is, which is what protects a bend the
 * attorney set: the write seam applies this again to the freshest set when its turn comes,
 * and a rule that minted a fresh bend every time would wipe one dragged in between.
 *
 * Straightening drops the bend rather than parking it, because the stored model has nowhere
 * to park it. Curving again mints a new one instead of restoring the old.
 */
export function setConnectorShape(
  definitions: Definitions,
  {
    fileId,
    id,
    shape,
    anchors,
    defaults,
  }: {
    fileId: string;
    id: string;
    shape: ConnectorShape;
    /** How many points a curve bends at. Read only when the shape asked for is a curve. */
    anchors: ConnectorDefaults["anchors"];
    defaults: ConnectorDefaults;
  },
): Definitions {
  return withInstance(definitions, fileId, id, (instance) => {
    const connector = instance.connector;
    if (connector == null) return instance;
    // A line already drawn the way it was asked for is left exactly as it is, bends and all.
    // The comparison has to reach the anchor count as well as the shape, because a bow and
    // an S are both curves, and stopping at the shape would refuse to turn one into the
    // other.
    const asked =
      shape === "straight"
        ? connector.shape === "straight"
        : connector.shape === "curved" && connector.controls.length === anchors;
    if (asked) return instance;
    // The line the attorney asked for, wearing the head this one already wears. Curving
    // mints a fresh curve, so a bend nobody has dragged starts where the convention puts it;
    // but the head is a decision already made about this line, and the conventions describe
    // lines that do not exist yet.
    return {
      ...instance,
      connector: connectorFrom(
        { ...defaults, shape, anchors, arrowhead: connector.arrowhead },
        instance.pin,
        connector.tip,
      ),
    };
  });
}

/**
 * Put a head on a leader line or take it off.
 *
 * The head rides on the line it decorates, so a mark with no line has nowhere to keep one
 * and the change settles as nothing, which is what makes the order two queued changes
 * arrive in stop mattering.
 */
export function setArrowhead(
  definitions: Definitions,
  { fileId, id, on }: { fileId: string; id: string; on: boolean },
): Definitions {
  return withInstance(definitions, fileId, id, (instance) => {
    const connector = instance.connector;
    if (connector == null) return instance;
    return { ...instance, connector: { ...connector, arrowhead: on } };
  });
}

/**
 * Take a mark's leader line away, or give it one back.
 *
 * The head lives on the line, so taking the line away takes the head with it structurally;
 * there is no second field to clear and no order two queued changes can arrive in that
 * leaves a head behind.
 *
 * A mark that already has a line keeps the one it has, tip and all, so a line the attorney
 * aimed survives this change arriving twice.
 */
export function setConnectorPresence(
  definitions: Definitions,
  {
    fileId,
    id,
    present,
    defaults,
  }: {
    fileId: string;
    id: string;
    present: boolean;
    defaults: ConnectorDefaults;
  },
): Definitions {
  return withInstance(definitions, fileId, id, (instance) => {
    if ((instance.connector != null) === present) return instance;
    if (!present) return { ...instance, connector: null };
    return {
      ...instance,
      connector: connectorFrom(
        defaults,
        instance.pin,
        defaultTip(instance.pin),
      ),
    };
  });
}

/**
 * The distinct elements pinned to one drawing, in the order they were first pinned.
 *
 * Distinct because the reuse controls offer an element to choose, and an element pinned twice
 * is one part with two marks rather than two things to pick between.
 */
export function elementsOn(definitions: Definitions, fileId: string): string[] {
  const here = definitions.figures.find((figure) => figure.file_id === fileId);
  if (here === undefined) return [];
  return [...new Set(instancesOf(here).map((i) => i.element_id))];
}

/** How far an element has got, which is what the drawing and the table both draw it from. */
export type ElementState = "needs-title" | "titled" | "confirmed";

/**
 * How this element reads.
 *
 * A blank title outranks confirmation, because the status projection needs both before it
 * calls a figure complete. Drawing such an element as confirmed would show a finished part on
 * a figure the same rules call unfinished. An id with no row at all reads the same way,
 * matching the projection, which treats a missing row as neither titled nor confirmed.
 */
export function elementState(
  definitions: Definitions,
  elementId: string,
): ElementState {
  const row = definitions.registry.find((r) => r.element_id === elementId);
  if (row === undefined || blank(row.title)) return "needs-title";
  return row.confirmed ? "confirmed" : "titled";
}

/** An element the set already holds, offered against what the attorney is typing. */
export interface TitleMatch {
  elementId: string;
  title: string;
  fileIds: string[];
}

/**
 * How many hits the field offers at once.
 *
 * A lookup is read at a glance while typing. Past a handful the list stops narrowing the
 * choice and starts asking to be scrolled, and typing one more letter is the faster move.
 */
const MOST_MATCHES = 8;

/**
 * The titles the set already holds that extend what the attorney has typed so far.
 *
 * Titling is free text over a registry shared by every drawing, so the same part can be
 * named twice under two elements without anything noticing. This is what the field offers
 * instead: the elements already carrying a title that starts with the typed text, each with
 * the drawings that pin it, so the attorney can reuse one rather than mint a twin.
 *
 * A pure read of the registry, so it answers on every keystroke without a round trip. It does
 * not project numbering: digits are a display concern of the offer list, which already holds
 * the pane's map. An empty field offers nothing at all, which is also what keeps the rows
 * still awaiting a title out of the list, since every string starts with the empty one.
 *
 * ``exact`` decides what a fully typed title does, which is the one thing the two callers
 * disagree on. Titling a drawing leaves it out, because offering back what is already spelled
 * proposes an edit that changes nothing. Naming an element in a claim keeps it, because there
 * the fully typed title is the answer being waited for.
 *
 * ``leading`` names the matches to list first, in registry order, ahead of the rest. They lead
 * before the list is cut to its length, so one of them is never left out behind the others.
 */
export function titleMatches(
  definitions: Definitions,
  typed: string,
  {
    exact = false,
    leading,
  }: { exact?: boolean; leading?: (elementId: string) => boolean } = {},
): TitleMatch[] {
  const wanted = typed.trim().toLowerCase();
  if (wanted === "") return [];
  const found = definitions.registry.filter((row) => {
    const title = row.title.trim().toLowerCase();
    // A group names a set, and handing marks to it would pin the set itself.
    const offerable = row.kind !== "group" && title.startsWith(wanted);
    return offerable && (exact || title !== wanted);
  });
  // Put first before the cut, so a match that leads is never one the cut drops.
  const leads = leading ?? (() => false);
  const ordered = [...found].sort(
    (a, b) => Number(!leads(a.element_id)) - Number(!leads(b.element_id)),
  );
  return ordered.slice(0, MOST_MATCHES).map((row) => ({
    elementId: row.element_id,
    title: row.title,
    fileIds: figuresSharing(definitions, row.element_id),
  }));
}

/** Which drawings carry this element, in the order the set holds them. */
export function figuresSharing(
  definitions: Definitions,
  elementId: string,
): string[] {
  return definitions.figures
    .filter((figure) =>
      instancesOf(figure).some((i) => i.element_id === elementId),
    )
    .map((figure) => figure.file_id);
}

/**
 * How many drawings carry each element, counted in one pass.
 *
 * `figuresSharing` answers for one element by walking every figure and every pin on
 * it. A reader asking for a whole claim set asks tens of times per keystroke and
 * once per drawn word on every render, which is that walk over again each time.
 * A figure pinning one element twice still counts once, as `figuresSharing` says.
 */
export function drawnOn(definitions: Definitions): Map<string, number> {
  const counted = new Map<string, number>();
  for (const figure of definitions.figures)
    for (const elementId of new Set(
      instancesOf(figure).map((instance) => instance.element_id),
    ))
      counted.set(elementId, (counted.get(elementId) ?? 0) + 1);
  return counted;
}

/** How many of one drawing's distinct elements are confirmed, and how many there are. */
export function confirmedOn(
  definitions: Definitions,
  fileId: string,
): { confirmed: number; total: number } {
  const ids = elementsOn(definitions, fileId);
  // Counted by the state the drawing draws rather than by the stored flag. A set from
  // elsewhere can carry a confirmed row with no title, which these rules will not build but
  // the API will store, and a readout saying it is done beside a pin saying it needs a title
  // gives the attorney nothing to believe.
  const confirmed = ids.filter(
    (elementId) => elementState(definitions, elementId) === "confirmed",
  );
  return { confirmed: confirmed.length, total: ids.length };
}

/**
 * The elements on one drawing that a second mark can be made from.
 *
 * Only settled ones. A repeat says the same part appears twice on the same drawing, and an
 * element still waiting for its title or its confirmation has not been agreed to say
 * anything yet, so offering it to be doubled spreads a decision nobody has taken.
 */
export function repeatable(definitions: Definitions, fileId: string): string[] {
  return elementsOn(definitions, fileId).filter(
    (elementId) => elementState(definitions, elementId) === "confirmed",
  );
}

/** One drawing's offer to another: which of its elements that drawing lacks. */
export interface BorrowSource {
  fileId: string;
  elementIds: string[];
}

/**
 * What each of the other drawings can lend this one, in the order the set holds them.
 *
 * Identity is shared across the set, so borrowing is how one part comes to be marked on two
 * drawings as one element rather than as two. A drawing already carrying the element is
 * offered it by nobody, since a borrow would place a repeat while saying it places a share,
 * and a drawing with nothing left to lend is left out rather than offered as an empty list.
 *
 * Only a confirmed element is lent. Borrowing a part whose title is still in flux propagates
 * that title with no signal that it is about to change; confirmation is what settles it. The
 * gate is on the element rather than on its drawing, because whether the drawing is walked
 * through says nothing about the title, and a fresh drawing needs to reuse what the rest of
 * the set already holds.
 */
export function borrowable(
  definitions: Definitions,
  fileId: string,
): BorrowSource[] {
  const here = new Set(elementsOn(definitions, fileId));
  // One pass over the registry, then constant-time reads per element: this runs
  // unmemoised on every render of the reuse menu, and reading each element's row by
  // a linear search would multiply the registry by every element on every drawing.
  const confirmed = new Set(
    definitions.registry
      .filter((row) => row.confirmed && !blank(row.title))
      .map((row) => row.element_id),
  );
  // This drawing is read like any other and falls out on its own: everything it could lend
  // it already carries, so its offer empties and the last filter drops it.
  return definitions.figures
    .map((figure) => ({
      fileId: figure.file_id,
      elementIds: elementsOn(definitions, figure.file_id).filter(
        (elementId) => !here.has(elementId) && confirmed.has(elementId),
      ),
    }))
    .filter((source) => source.elementIds.length > 0);
}

/** Absent, or partly absent, read as the empty set at version zero. */
export function definitionsOf(
  definitions: FigureListResponse["definitions"] | undefined,
): Definitions {
  return {
    registry: definitions?.registry ?? [],
    figures: definitions?.figures ?? [],
    // A set stored before the conventions existed carries none, and reads as made under the
    // ruled ones rather than as having none, which nothing downstream could draw.
    settings: settingsOf(definitions?.settings),
    version: definitions?.version ?? 0,
  };
}
